import { type ReactNode } from 'react';
import { NavLink, Navigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Activity,
  Settings,
  AlertTriangle,
  MessageSquare,
  Shield,
  LogOut,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '../../auth/AuthProvider';

const ADMIN_EMAIL = 'admin@ncide.com';

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/admin/users', label: 'Users', icon: Users },
  { to: '/admin/payments', label: 'Payments', icon: CreditCard },
  { to: '/admin/health', label: 'Health', icon: Activity },
  { to: '/admin/config', label: 'Config', icon: Settings },
  { to: '/admin/risks', label: 'Risks', icon: AlertTriangle },
  { to: '/admin/telegram-accounts', label: 'Telegram Accounts', icon: MessageSquare },
];

function SidebarLink({ item }: { item: NavItem }): JSX.Element {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/admin'}
      className={({ isActive }) =>
        clsx(
          'flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors',
          isActive
            ? 'bg-brand-primary text-white'
            : 'text-slate-600 hover:bg-slate-100 hover:text-brand-ink',
        )
      }
    >
      <Icon size={18} />
      <span>{item.label}</span>
    </NavLink>
  );
}

export function AdminLayout(): JSX.Element {
  const { user, signOut } = useAuth();

  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Top navbar */}
      <header className="w-full bg-white border-b border-slate-200 h-14 flex items-center px-6 gap-4 z-20 shadow-sm">
        <div className="flex items-center gap-2 mr-6">
          <Shield size={22} className="text-brand-primary" />
          <span className="text-lg font-bold text-brand-ink">teleclaw Admin</span>
        </div>
        <div className="flex-1" />
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-primary/10 text-brand-primary text-xs font-semibold">
            <Shield size={12} />
            {ADMIN_EMAIL}
          </span>
          <button
            onClick={() => void signOut()}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-brand-danger transition-colors"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar */}
        <aside className="w-56 bg-white border-r border-slate-200 flex flex-col py-4 px-3 gap-1 shrink-0">
          {NAV_ITEMS.map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
